import { RecommendedCarrier } from "../api/types/carrier-recommendation-model";

export function getEffectiveVendorName(
  carrier: RecommendedCarrier,
  labelToVendorCode: Record<string, string> = {}
): string {
  // Bold Penguin override: resolve vendor via config-supplied label map
  if (carrier.isBoldPenguinOverride && carrier.label) {
    const key = carrier.label.toLowerCase();
    return labelToVendorCode[key] ?? carrier.carrier.toUpperCase();
  }
  // Generic Bold Penguin marketplace (both casing variants from the API)
  if (carrier.carrier === "BoldPenguin" || carrier.carrier === "BOLD_PENGUIN") {
    return "BOLDPENGUIN";
  }
  return carrier.carrier.toUpperCase();
}

export function isVendorAllowedByFlag(
  carrier: RecommendedCarrier,
  lob: string,
  vendorsByLob?: Record<string, string[]>,
  labelToVendorCode: Record<string, string> = {}
): boolean {
  if (!vendorsByLob || Object.keys(vendorsByLob).length === 0) return true;

  const lobKey = lob === "OWNERSONLYWC" ? "WC" : lob;
  const allowedVendors = vendorsByLob[lobKey];
  if (!allowedVendors || allowedVendors.length === 0) return true;
  if (allowedVendors.length === 1 && allowedVendors[0].toUpperCase() === "NONE") return false;
  return allowedVendors
    .map((v) => v.toUpperCase())
    .includes(getEffectiveVendorName(carrier, labelToVendorCode));
}

export function filterCarriersByVendorFlag(
  lobMap: Map<string, RecommendedCarrier[]>,
  availableVendors?: Record<string, string[]>,
  labelToVendorCode: Record<string, string> = {}
): Map<string, RecommendedCarrier[]> {
  if (!availableVendors || Object.keys(availableVendors).length === 0) return lobMap;

  const filteredMap = new Map<string, RecommendedCarrier[]>();
  lobMap.forEach((carriers, lob) => {
    const filteredCarriers = carriers.filter((carrier) =>
      isVendorAllowedByFlag(carrier, lob, availableVendors, labelToVendorCode)
    );
    if (filteredCarriers.length > 0) {
      filteredMap.set(lob, filteredCarriers);
    }
  });
  return filteredMap;
}

export function sortRedirectableVendorsFirst(
  lobMap: Map<string, RecommendedCarrier[]>,
  redirectableVendors?: Record<string, string[]>,
  labelToVendorCode: Record<string, string> = {}
): Map<string, RecommendedCarrier[]> {
  if (!redirectableVendors || Object.keys(redirectableVendors).length === 0) return lobMap;

  const sortedMap = new Map<string, RecommendedCarrier[]>();
  lobMap.forEach((carriers, lob) => {
    sortedMap.set(
      lob,
      [...carriers].sort((a, b) => {
        const aRedirectable = isVendorAllowedByFlag(a, lob, redirectableVendors, labelToVendorCode) ? 0 : 1;
        const bRedirectable = isVendorAllowedByFlag(b, lob, redirectableVendors, labelToVendorCode) ? 0 : 1;
        return aRedirectable - bRedirectable;
      })
    );
  });
  return sortedMap;
}

export function isRedirectControl(isRedirectable?: boolean): boolean {
  return isRedirectable !== false;
}

export function isSecondPrimaryPill(index: number, isRedirectable?: boolean): boolean {
  return index === 0 && isRedirectControl(isRedirectable);
}

export function getBundlePartnerLayout<T extends { isRedirectable?: boolean }>(
  firstLobCarriers: T[] | undefined,
  secondLobCarriers: T[] | undefined,
  showProducts: boolean
) {
  const first = firstLobCarriers ?? [];
  const second = secondLobCarriers ?? [];
  const secondPrimary = second[0];
  const showSecondInBox = !showProducts || isRedirectControl(secondPrimary?.isRedirectable);
  return {
    firstPrimary: first[0],
    secondPrimary,
    showSecondInBox,
    firstOthers: first.slice(1),
    secondOthers: showProducts && !isRedirectControl(secondPrimary?.isRedirectable) ? second : second.slice(1),
  };
}
